import { ConnectionState, EnvironmentResult } from './environment-fan-out.service'
import { SupportEnvironment } from './support-environment'

export type EnvironmentTagged<T> = T & { environment: SupportEnvironment }

/**
 * Row ids are per-database primary keys, so the same id exists in every environment. Anything that
 * identifies a row across the merged set — track functions, selection, filter options — has to key
 * on this rather than on the bare id.
 */
export function scopedId(environment: SupportEnvironment, id: number | string): string {
  return `${environment.key}:${id}`
}

/**
 * The inverse of scopedId over a whole selection: an environment is only ever sent the ids that
 * belong to it, because the same number means a different row in each of the others.
 */
export function unscopeIds(selection: string[], environment: SupportEnvironment): number[] {
  const prefix = `${environment.key}:`
  return selection.filter((entry) => entry.startsWith(prefix)).map((entry) => Number(entry.slice(prefix.length)))
}

export function tagRows<T>(results: EnvironmentResult<T[]>[]): EnvironmentTagged<T>[] {
  return results.flatMap((result) => (result.data ?? []).map((row) => ({ ...row, environment: result.environment })))
}

// The rows and how many environments they speak for, together: rows alone cannot say whether an
// environment is missing from them.
export type FanOut<T> = {
  summary: EnvironmentSummary
  data: T
}

export function toFanOut<TResponse, TData>(results: EnvironmentResult<TResponse>[], merge: (results: EnvironmentResult<TResponse>[]) => TData): FanOut<TData> {
  return { summary: summarize(results), data: merge(results) }
}

export type EnvironmentSummary = {
  total: number
  connected: number
  isChecking: boolean
  // Distinct from "still checking": every environment has answered and none of them answered well.
  hasNothingToShow: boolean
  lastCheckedAt: Date | null
}

function tally(results: EnvironmentResult<unknown>[], state: ConnectionState): number {
  return results.filter((result) => result.state === state).length
}

// Ordered: the states a reader most needs to act on come first.
const TRAILING_STATES: [ConnectionState, (count: number) => string][] = [
  ['signInRequired', (count) => `${count} ${count === 1 ? 'needs' : 'need'} sign-in`],
  ['unreachable', (count) => `${count} unreachable`],
  ['notPermitted', (count) => `${count} not permitted`],
  ['checking', (count) => `${count} still checking`],
]

export function rollCall(results: EnvironmentResult<unknown>[]): string {
  const connected = tally(results, 'connected')
  if (connected === results.length) {
    return `All ${results.length} environments connected.`
  }
  const notes = TRAILING_STATES.map(([state, wording]) => ({ count: tally(results, state), wording }))
    .filter(({ count }) => count > 0)
    .map(({ count, wording }) => wording(count))
  return [`${connected} of ${results.length} connected`, ...notes].join(' · ')
}

// Ordered by how much the state needs acting on, so that merging several reads of the same
// environment keeps the one worth telling the reader about.
const STATE_SEVERITY: Record<ConnectionState, number> = {
  connected: 0,
  checking: 1,
  unreachable: 2,
  notPermitted: 3,
  signInRequired: 4,
}

/**
 * A screen reads each environment several times over. One request failing while the rest succeed is
 * a worse story than the reader needs, and one succeeding while the rest fail is a better one than
 * is true, so the banner speaks for the environment rather than for a request.
 *
 * The environments are passed in rather than read off the results so that the answer comes back in
 * the order they were configured in, and so that a read of something no longer configured is left
 * out rather than named.
 */
export function worstStatePerEnvironment(environments: SupportEnvironment[], results: EnvironmentResult<unknown>[]): EnvironmentResult<unknown>[] {
  return environments
    .map((environment) => results.filter((result) => result.environment.key === environment.key))
    .filter((forEnvironment) => forEnvironment.length > 0)
    .map((forEnvironment) => forEnvironment.reduce((worst, result) => (STATE_SEVERITY[result.state] > STATE_SEVERITY[worst.state] ? result : worst)))
}

/**
 * The environments whose rows are missing from whatever is on screen. Ones still being read are left
 * out: they answer within a moment of the page opening, and a warning that shows on every load only
 * to take itself away again is one the reader learns to look past.
 */
export function disconnectedEnvironments(results: EnvironmentResult<unknown>[]): EnvironmentResult<unknown>[] {
  return results.filter((result) => result.state !== 'connected' && result.state !== 'checking')
}

export function summarize(results: EnvironmentResult<unknown>[]): EnvironmentSummary {
  const connected = tally(results, 'connected')
  const checkedStamps = results.map((result) => result.checkedAt).filter((checkedAt): checkedAt is Date => checkedAt !== null)
  return {
    total: results.length,
    connected,
    isChecking: results.some((result) => result.state === 'checking'),
    hasNothingToShow: connected === 0 && !results.some((result) => result.state === 'checking'),
    lastCheckedAt: checkedStamps.length === 0 ? null : new Date(Math.max(...checkedStamps.map((stamp) => stamp.getTime()))),
  }
}
